import React from 'react';
import { AlertTriangle, Building2, Database, Globe, Zap } from 'lucide-react';

export const TAG_STYLES = {
  target: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  mid_market_fit: 'bg-blue-50 text-blue-700 border-blue-200',
  growth_stage: 'bg-sky-50 text-sky-700 border-sky-200',
  size_unknown: 'bg-gray-100 text-gray-500 border-gray-200',
  manual_override: 'bg-violet-50 text-violet-700 border-violet-200',
  strategic_enterprise: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  enterprise: 'bg-slate-100 text-slate-600 border-slate-200',
  oversized: 'bg-slate-100 text-slate-600 border-slate-200',
  hard_to_access: 'bg-rose-50 text-rose-600 border-rose-200',
  long_term_opportunity: 'bg-cyan-50 text-cyan-700 border-cyan-200',
};

export const TAG_LABELS = {
  target: 'Target', mid_market_fit: 'Mid-Market', growth_stage: 'Growth',
  size_unknown: 'Size Unknown', manual_override: 'Manual Override',
  strategic_enterprise: 'Strategic Enterprise', enterprise: 'Enterprise',
  oversized: 'Oversized', hard_to_access: 'Hard to Access',
  long_term_opportunity: 'Long-Term Opportunity',
};

export const TIER_STYLES = {
  best_immediate: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'Best Immediate', icon: Zap },
  needs_enrichment: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Needs Enrichment', icon: Database },
  strategic_enterprise: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', label: 'Strategic Enterprise', icon: Building2 },
  broader_qualified: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', label: 'Broader Qualified', icon: Globe },
};

export const TIER_ORDER = ['best_immediate', 'broader_qualified', 'needs_enrichment', 'strategic_enterprise'];

export const QualTag = ({ tag }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${TAG_STYLES[tag] || 'bg-gray-100 text-gray-600 border-gray-200'}`}
    data-testid={`tag-${tag}`}>
    {TAG_LABELS[tag] || tag}
  </span>
);

export const TierBadge = ({ tier }) => {
  const s = TIER_STYLES[tier] || TIER_STYLES.broader_qualified;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${s.bg} ${s.text} border ${s.border}`} data-testid={`tier-${tier}`}>
      {s.label}
    </span>
  );
};

export const ConfidenceBadge = ({ confidence, flags }) => {
  if (!confidence || confidence === 'high') return null;
  const isLow = confidence === 'low';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${
        isLow
          ? 'bg-red-50 text-red-700 border-red-200'
          : 'bg-amber-50 text-amber-700 border-amber-200'
      }`}
      data-testid={`confidence-${confidence}`}
      title={flags?.join(' | ') || `Firmographic confidence: ${confidence}`}
    >
      <AlertTriangle className="h-3 w-3" />
      {isLow ? 'Low Confidence' : 'Verify Data'}
    </span>
  );
};

export const EnrichmentBadge = ({ ep }) => {
  if (!ep?.flagged) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid="enrichment-priority-badge"
      title={ep.reasons?.join(', ')}>
      <Database className="h-3 w-3" /> Needs Enrichment
    </span>
  );
};

export const TierFilterBar = ({ activeTier, onTierChange, tierSummary, showStrategic = false }) => {
  const tiers = showStrategic ? TIER_ORDER : TIER_ORDER.filter(t => t !== 'strategic_enterprise');
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="tier-filter-bar">
      <button
        onClick={() => onTierChange('')}
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
          !activeTier ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
        }`}
        data-testid="tier-filter-all"
      >
        All
      </button>
      {tiers.map(tier => {
        const s = TIER_STYLES[tier];
        const count = tierSummary?.[tier] || 0;
        return (
          <button
            key={tier}
            onClick={() => onTierChange(tier)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
              activeTier === tier ? `${s.bg} ${s.text} ${s.border} ring-1 ring-offset-1` : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`tier-filter-${tier}`}
          >
            {s.label} {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
          </button>
        );
      })}
    </div>
  );
};

export const filterByTier = (items, activeTier, showStrategic = false) => {
  let filtered = items;
  if (!showStrategic) {
    filtered = filtered.filter(i => i.priority_tier !== 'strategic_enterprise');
  }
  if (activeTier) {
    filtered = filtered.filter(i => i.priority_tier === activeTier);
  }
  return filtered;
};
